// @flow
import * as csx from 'csstips';
import classify from '@src/utils/typestyle';
import { classes as composeClasses } from 'typestyle';

import type { DiamondType } from '@types/diamond';

import {
  borderGrayOutline,
  buttonBlueBackground,
  linkBlueText
} from '@src/styles/general';

import DiamondInBand from '@src/components/productDetail/images/DiamondInBand';
import EnlargedDiamond from '@src/components/productDetail/images/EnlargedDiamond';
import Caption from '@components/productDetail/images/Caption';
import FavoriteIcon from '../FavoriteIcon';

const rolloverAS = '/images/rollovers/rollover_as.gif';
const rolloverB = '/images/rollovers/rollover_b.gif';
const rolloverC = '/images/rollovers/rollover_c.gif';
const rolloverE = '/images/rollovers/rollover_e.gif';
const rolloverH = '/images/rollovers/rollover_h.gif';
const rolloverM = '/images/rollovers/rollover_m.gif';
const rolloverO = '/images/rollovers/rollover_o.gif';
const rolloverP = '/images/rollovers/rollover_p.gif';
const rolloverPR = '/images/rollovers/rollover_pr.gif';
const rolloverR = '/images/rollovers/rollover_r.gif';

const rolloverImages: { [string]: string } = {
  AS: rolloverAS,
  B: rolloverB,
  C: rolloverC,
  E: rolloverE,
  H: rolloverH,
  M: rolloverM,
  O: rolloverO,
  P: rolloverP,
  PR: rolloverPR,
  R: rolloverR
};

const classes = classify({
  container: {
    textAlign: 'center'
  },
  imageSquareContainer: {
    width: '100%',
    paddingTop: '100%',
    position: 'relative'
  },
  imageContainer: {
    ...csx.horizontal,
    ...csx.center,
    ...csx.centerJustified,
    border: `1px solid ${borderGrayOutline}`,
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    overflow: 'hidden'
  },
  image: {
    maxWidth: '100%',
    maxHeight: '100%'
  },
  link: {
    color: linkBlueText
  },
  sampleImage: {
    marginTop: '12px',
    marginBottom: '13px',
    fontSize: '13px',
    lineHeight: '16px',
    textAlign: 'center',
    color: '#908e8e'
  },
  buttonGroup: {
    border: '1px solid #ebebeb',
    borderRadius: '5px',
    display: 'inline-block'
  },
  leftButton: {
    borderRadius: '5px 0 0 5px'
  },
  rightButton: {
    borderRadius: '0 5px 5px 0'
  },
  button: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    textTransform: 'none',
    padding: '7px 15px 7px 15px',
    height: 'auto'
  },
  inactiveButton: {
    backgroundColor: 'white',
    color: '#7f7f7f',
    '&:hover': {
      color: 'white'
    }
  },
  activeButton: {
    backgroundColor: buttonBlueBackground,
    color: 'white',
    '&:hover': {
      color: 'white'
    }
  },
  favoriteLink: {
    position: 'absolute',
    bottom: 2,
    right: 2
  }
});

type PropsType = {
  diamond: DiamondType,
  hoveredSpec: string | null,
  showInARingButton?: boolean,
  onFavoriteToggle: () => void,
  isFavorited: boolean,
  dyor: boolean
};

type StateType = {
  currentImage: string,
  bandColor: string
};

class DiamondProductImages extends React.Component<PropsType, StateType> {
  state = {
    currentImage: 'enlarged',
    bandColor: 'yellow'
  };

  static defaultProps = {
    showInARingButton: true
  };

  handleToggleBandClick = (): void => {
    this.setState((prevState) => {
      return {
        bandColor: prevState.bandColor === 'yellow' ? 'white' : 'yellow'
      };
    });
  };

  handleImageChange = (type: string) => {
    this.setState({
      currentImage: type
    });
  };

  standardImage = () => {
    return this.state.currentImage === 'enlarged' ? (
      <EnlargedDiamond diamond={this.props.diamond} className={classes.image} />
    ) : (
      <DiamondInBand
        metal={`${this.state.bandColor}Gold`}
        diamond={this.props.diamond}
        scale={1}
      />
    );
  };

  hoveredImage = () => {
    const url: string | null = rolloverImages[this.props.diamond.shape];
    let top;

    switch (this.props.hoveredSpec) {
      case 'length':
        top = 0;
        break;
      case 'width':
        top = -258;
        break;
      case 'depth':
        top = -516;
        break;
      case 'lw_ratio':
        top = -774;
        break;
      case 'depth_percent':
        top = -1032;
        break;
      case 'table_percent':
        top = -1290;
        break;
      case 'culet':
        top = -1548;
        break;
      case 'girdle':
        top = -1806;
        break;
      default:
        return null;
    }

    // Add a transparent image src otherwise chrome adds a border
    return (
      <img
        style={{
          height: '258px',
          width: '276px',
          backgroundImage: `url("${String(url)}")`,
          backgroundPosition: `0px ${top}px`
        }}
        src={
          'data:image/gif;charset=utf-8;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
        }
      />
    );
  };

  render () {
    return (
      <div className={classes.container}>
        <div className={classes.imageSquareContainer}>
          <div className={classes.imageContainer}>
            {this.hoveredImage() || this.standardImage()}
          </div>
          {this.props.hoveredSpec ? null : (
            <FavoriteIcon
              className={classes.favoriteLink}
              onClick={this.props.onFavoriteToggle}
              selected={this.props.isFavorited}
            />
          )}
        </div>
        <Caption
          captionType={this.state.currentImage}
          onToggleBandClick={this.handleToggleBandClick}
          bandColor={this.state.bandColor}
          hoveredSpec={this.props.hoveredSpec}
          linkUrl={this.props.diamond.vendor_image}
        />
        {this.props.showInARingButton && (
          <div className={classes.buttonGroup}>
            <button
              className={composeClasses(
                classes.button,
                classes.leftButton,
                this.state.currentImage === 'enlarged'
                  ? classes.activeButton
                  : classes.inactiveButton
              )}
              onClick={() => this.handleImageChange('enlarged')}>
              Enlarged
            </button>
            <button
              className={composeClasses(
                classes.button,
                classes.rightButton,
                this.state.currentImage === 'ring'
                  ? classes.activeButton
                  : classes.inactiveButton
              )}
              onClick={() => this.handleImageChange('ring')}>
              In a Ring
            </button>
          </div>
        )}
      </div>
    );
  }
}

export default DiamondProductImages;
